/**
 * useIdleTimeout Hook
 * 
 * Security best practice: Automatically signs out users after inactivity.
 * 
 * Features:
 * - Configurable inactivity timeout (default 15 minutes)
 * - 60 second warning before logout
 * - Resets on clicks, keypress, and scroll
 * - Works across all user roles
 * - Settings stored in localStorage
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const WARNING_TIME = 60 * 1000; // 60 seconds warning before logout
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];
const SETTINGS_KEY = 'idle_timeout_settings';

// Default settings
const DEFAULT_SETTINGS = {
  enabled: true,
  timeoutMinutes: 15 // Default 15 minutes
};

// Get settings from localStorage
export const getIdleTimeoutSettings = () => {
  try {
    const stored = localStorage.getItem(SETTINGS_KEY);
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.error('Error reading idle timeout settings:', e);
  }
  return DEFAULT_SETTINGS;
};

// Save settings to localStorage
export const saveIdleTimeoutSettings = (settings) => {
  try {
    const current = getIdleTimeoutSettings();
    const updated = { ...current, ...settings };
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(updated));
    // Dispatch event to notify other components
    window.dispatchEvent(new CustomEvent('idle-timeout-settings-changed', { detail: updated }));
    return updated;
  } catch (e) {
    console.error('Error saving idle timeout settings:', e);
    return getIdleTimeoutSettings();
  }
};

export const useIdleTimeout = (onLogout, forceEnabled = null) => {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(60);
  const [isIdle, setIsIdle] = useState(false);
  const [settings, setSettings] = useState(() => getIdleTimeoutSettings());
  
  // Use refs for values needed in timers/handlers to avoid stale closures
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const showWarningRef = useRef(false);
  const onLogoutRef = useRef(onLogout);
  const settingsRef = useRef(settings);

  // Keep refs in sync
  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  // Determine if enabled
  const isEnabled = forceEnabled !== null ? forceEnabled : settings.enabled;
  const timeoutMs = settings.timeoutMinutes * 60 * 1000;

  // Re-read settings on mount and when they change
  useEffect(() => {
    setSettings(getIdleTimeoutSettings());
    
    const handleSettingsChange = (event) => {
      const newSettings = event.detail || getIdleTimeoutSettings();
      setSettings(newSettings);
    };
    
    const handleStorageChange = (event) => {
      if (event.key === SETTINGS_KEY) {
        setSettings(getIdleTimeoutSettings());
      }
    };
    
    window.addEventListener('idle-timeout-settings-changed', handleSettingsChange);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('idle-timeout-settings-changed', handleSettingsChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  }, []);

  // Start the timers
  const startTimers = useCallback(() => {
    const currentSettings = settingsRef.current;
    const currentTimeoutMs = currentSettings.timeoutMinutes * 60 * 1000;
    const warningDelay = Math.max(currentTimeoutMs - WARNING_TIME, 10000);

    // Set warning timeout
    warningTimeoutRef.current = setTimeout(() => {
      console.log('⚠️ Warning timeout triggered');
      showWarningRef.current = true;
      setShowWarning(true);
      setRemainingTime(60);
      
      // Start countdown
      countdownRef.current = setInterval(() => {
        setRemainingTime(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, warningDelay);

    // Set logout timeout
    timeoutRef.current = setTimeout(() => {
      console.log('🔒 Logout timeout triggered');
      setIsIdle(true);
      setShowWarning(false);
      showWarningRef.current = false;
      if (onLogoutRef.current) onLogoutRef.current();
    }, currentTimeoutMs);
  }, []);

  // Reset the idle timer
  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIsIdle(false);
    showWarningRef.current = false;
    setShowWarning(false);
    setRemainingTime(60);
    clearAllTimers();
    
    const currentSettings = settingsRef.current;
    const currentEnabled = forceEnabled !== null ? forceEnabled : currentSettings.enabled;
    
    if (currentEnabled) {
      startTimers();
    }
  }, [clearAllTimers, startTimers, forceEnabled]);

  // Continue session (called from warning modal)
  const continueSession = useCallback(() => {
    console.log('✅ Continue session - resetting timer');
    resetTimer();
  }, [resetTimer]);

  // Handle user activity
  const handleActivity = useCallback(() => {
    if (!showWarningRef.current) {
      resetTimer();
    }
  }, [resetTimer]);

  // Get time until warning
  const getTimeUntilWarning = useCallback(() => {
    const elapsed = Date.now() - lastActivityRef.current;
    const remaining = timeoutMs - WARNING_TIME - elapsed;
    return Math.max(0, Math.floor(remaining / 1000));
  }, [timeoutMs]);

  // Setup/teardown effect
  useEffect(() => {
    if (!isEnabled) {
      clearAllTimers();
      setShowWarning(false);
      showWarningRef.current = false;
      return;
    }

    // Add activity listeners
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Start timers
    console.log(`🕐 Idle timeout active: ${settings.timeoutMinutes} minutes`);
    startTimers();

    // Cleanup
    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearAllTimers();
    };
  }, [isEnabled, settings.timeoutMinutes]); // Only depend on actual setting values

  // Handle countdown reaching zero
  useEffect(() => {
    if (remainingTime === 0 && showWarning) {
      console.log('⏰ Countdown reached zero - logging out');
      setShowWarning(false);
      showWarningRef.current = false;
      setIsIdle(true);
      if (onLogoutRef.current) onLogoutRef.current();
    }
  }, [remainingTime, showWarning]);

  return {
    showWarning,
    remainingTime,
    isIdle,
    continueSession,
    resetTimer,
    getTimeUntilWarning,
    settings,
    isEnabled,
    timeoutMinutes: settings.timeoutMinutes
  };
};

export default useIdleTimeout;
